import React, { useContext, useEffect } from 'react';
import {
  Avatar,
  Button,
  EnhancedFilterDropdown,
  Icon,
  message,
  Space,
  TableProps,
  Tag,
  Tooltip,
} from 'components';
import { useBreakpoint } from 'hooks';
import styled from 'styled-components';
import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';
import { FormattedOrgForAppointmentDeskType } from 'components/UniversalSearch/types';
import { MLS_TYPE_LABEL_MAP } from 'components/UniversalSearch/constants';
import { CrossOrgAnsweringContext } from 'contexts';
import { createFormatDateFunction } from 'utils';
import { orgs as searchOrgs, SearchOrgs } from 'api/SearchController/orgs';
import errorReporter from '@app/errorReporter';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import {
  addDatacy,
  addDatacyToHeader,
  assume,
  getStatusTagColor,
  getSubscriptionTagTextAndColor,
} from './utils';
import SearchTable from './SearchTable';
import AnsweringSearchContext from './AnsweringSearchContext';
import { useSearchParams } from './hooks';

const LIMIT = 20;

const AnsweringBrokerageTable = () => {
  const { md } = useBreakpoint();
  const { answeringConfirm } = useContext(CrossOrgAnsweringContext);

  const {
    mlsFilterOptions,
    text: debouncedText,
    page,
    setPage,
    isMls,
    total,
    setTotal,
    setInitialTotal,
    generateCsvDownload,
    isLoading,
    setIsLoading,
    hasValidStripeMlsProductIdForOrg,
  } = React.useContext(AnsweringSearchContext);
  const [searchParam, setSearchParam] = useSearchParams<{
    mlsTypesFilter: string[];
    subscriptionTagsFilter: string[];
    planTypeFilter: string[];
    statusFilter: string[];
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }>();
  const [brokerages, setBrokerages] = React.useState<SearchOrgs['orgs']>();
  const [mlsTypesFilter, setMlsTypesFilter] = React.useState<string[]>(
    searchParam.mlsTypesFilter || [],
  );
  const [subscriptionTagsFilter, setSubscriptionTagsFilter] = React.useState<
    string[]
  >(searchParam.subscriptionTagsFilter || []);
  const [planTypeFilter, setPlanTypeFilter] = React.useState<string[]>(
    searchParam.planTypeFilter || [],
  );
  const [statusFilter, setStatusFilter] = React.useState<string[]>(
    searchParam.statusFilter || [],
  );
  const [sortDirection, setSortDirection] = React.useState<
    'asc' | 'desc' | undefined
  >(searchParam.sortDirection);
  const [sortField, setSortField] = React.useState<string | undefined>(
    searchParam.sortField || 'name',
  );
  const isRendered = React.useRef(false);
  const formatDateStr = createFormatDateFunction('MMM d, yyyy');

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const searchOptions = {
          input: debouncedText,
          from: page * LIMIT,
          sortField: sortField || 'name',
          plans: planTypeFilter,
          statuses: statusFilter,
          sortDir: sortDirection ?? 'asc',
          ...(hasValidStripeMlsProductIdForOrg
            ? {
                answeringServiceTypes: subscriptionTagsFilter as (
                  | AnsweringServiceType
                  | 'ns'
                  | 'on'
                )[],
              }
            : subscriptionTagsFilter?.length === 1 && {
                answeringSubscribed: subscriptionTagsFilter[0] === 'on',
              }),
        };
        generateCsvDownload({ dataType: 'brokerage', ...searchOptions });
        const {
          orgs,
          count,
          total: totalRes,
        } = await searchOrgs({
          ...searchOptions,
          limit: LIMIT,
          fetchUnconfigured: true,
          ...(!isMls && { mlsTypes: mlsTypesFilter }),
        });
        setBrokerages(orgs);
        setTotal(count);
        if (isMls) {
          setInitialTotal(totalRes!);
        }
      } catch (e) {
        message.error('Error fetching brokerages data');
        errorReporter.error('Error fetching data', e);
      }
      setIsLoading(false);
      // Trigger dummy resize to trigger useTableResizer to fetch correct tableHeaderHeight after data loads
      window.dispatchEvent(new Event('resize'));
    })();
  }, [
    debouncedText,
    mlsTypesFilter,
    subscriptionTagsFilter,
    planTypeFilter,
    statusFilter,
    sortDirection,
    sortField,
    page,
    hasValidStripeMlsProductIdForOrg,
  ]);

  useEffect(() => {
    if (!isRendered.current) {
      isRendered.current = true;
      return;
    }
    setPage(0);
    setSearchParam({
      mlsTypesFilter,
      subscriptionTagsFilter,
      planTypeFilter,
      statusFilter,
      sortDirection,
      sortField,
    });
  }, [
    debouncedText,
    mlsTypesFilter,
    subscriptionTagsFilter,
    sortDirection,
    planTypeFilter,
    statusFilter,
    sortField,
  ]);

  const columns: TableProps['columns'] = ([
    {
      width: 220,
      title: 'Brokerage',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
      sortOrder:
        sortDirection && sortField === 'name' ? `${sortDirection}end` : null,
      showSorterTooltip: true,
      render: (name: string, { logoUrl }) => (
        <Space align="center" size="middle">
          <Avatar src={logoUrl} />
          {name}
        </Space>
      ),
      onCell: addDatacy('brokerages-name'),
      onHeaderCell: addDatacyToHeader('brokerages-name'),
    },
    {
      width: 120,
      title: 'Office IDs',
      key: 'oids',
      dataIndex: 'oids',
      render: (oids: string[], record: any) => {
        const officeNames = record.officeName as string[] | undefined;
        return (
          <FlexCol style={{ color: '#000000', fontSize: '14px', fontFamily: 'Inter', fontStyle: 'normal' }}>
            {oids?.map((oid, idx) => (
              <span key={idx}>
                {officeNames?.[idx] && (
                  <span style={{ fontWeight: 500 }}>{officeNames[idx]}<br /></span>
                )}
                OID: {oid}
              </span>
            ))}
          </FlexCol>
        );
      },
      onCell: addDatacy('brokerages-oids'),
      onHeaderCell: addDatacyToHeader('brokerages-oids'),
    },
    {
      width: 120,
      title: (
        <Tooltip
          placement="top"
          title={
            'This column represents the amount of fully configured listings out of the total listings within this org'
          }
        >
          <Space>Configured</Space>
        </Tooltip>
      ),
      key: 'configured',
      dataIndex: 'unconfigured',
      onCell: addDatacy('configured'),
      onHeaderCell: addDatacyToHeader('brokerages-configured'),
      render: ({ unconfiguredCount, totalCount }) => (
        <div>{`${totalCount - unconfiguredCount} / ${totalCount} (${
          totalCount
            ? Math.round(((totalCount - unconfiguredCount) / totalCount) * 100)
            : 0
        }%)`}</div>
      ),
    },
    !isMls && {
      width: 100,
      title: 'Main Phone',
      key: 'phone',
      dataIndex: 'phone',
      onCell: addDatacy('brokerages-phone'),
      onHeaderCell: addDatacyToHeader('brokerages-phone'),
    },
    !isMls && {
      width: 100,
      title: 'MLS',
      key: 'mlsTypes',
      dataIndex: 'mlsTypes',
      filters: mlsFilterOptions,
      filteredValue: mlsTypesFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown
          {...props}
          okText="Apply"
          resetText="Clear"
          allowSearch
        />
      ),
      render: (mlsTypes: FormattedOrgForAppointmentDeskType['mlsTypes']) =>
        Array.isArray(mlsTypes) &&
        mlsTypes
          .map(mlsType => MLS_TYPE_LABEL_MAP[mlsType] ?? mlsType)
          .join(', '),
      onCell: addDatacy('brokerages-mls'),
      onHeaderCell: addDatacyToHeader('brokerages-mls'),
    },
    {
      width: 120,
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      onCell: addDatacy('brokerages-status'),
      onHeaderCell: addDatacyToHeader('brokerages-status'),
      filters: [
        {
          text: (
            <>
              Active
              <Tooltip
                title="The brokerage has fully launched & is active on the BrokerBay platform."
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'active',
        },
        {
          text: (
            <>
              Onboarding
              <Tooltip
                title="The brokerage has selected a launch date but is not yet active on the BrokerBay platform."
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'onboarding',
        },
        {
          text: (
            <>
              Unclaimed
              <Tooltip
                title="This brokerage has yet to be claimed on the BrokerBay platform."
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'unclaimed',
        },
      ],
      filteredValue: statusFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Clear" />
      ),
      render: status => (
        <StyledTag color={getStatusTagColor(status)}>
          {status.toLowerCase()}
        </StyledTag>
      ),
    },
    isMls && {
      width: 120,
      title: 'Plan Type',
      key: 'plan-type',
      dataIndex: 'plan',
      filters: [
        {
          text: (
            <>
              Brokerage
              <Tooltip
                title="This brokerage has full access to features on the BrokerBay platform"
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'brokerage',
        },
        {
          text: (
            <>
              Agent
              <Tooltip
                title="This brokerage has a limited view of the features on the BrokerBay platform"
                placement={'bottom'}
              >
                {' '}
                <Icon
                  type="antdInfoCircleOutlined"
                  color="info"
                  width={13.5}
                />{' '}
              </Tooltip>
            </>
          ),
          value: 'agent',
        },
      ],
      filteredValue: planTypeFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Clear" />
      ),
      onCell: addDatacy('brokerages-plan-type'),
      onHeaderCell: addDatacyToHeader('brokerages-plan-type'),
    },
    {
      width: hasValidStripeMlsProductIdForOrg ? 150 : 130,
      title:
        isMls && !hasValidStripeMlsProductIdForOrg
          ? 'Concierge'
          : 'Subscription',
      key: 'subscriptionTag',
      dataIndex: 'subscriptionTag',
      filters: hasValidStripeMlsProductIdForOrg
        ? [
            {
              text: 'Live Concierge',
              value: AnsweringServiceType.LiveConciergePlus,
            },
            {
              text: 'Appt Desk Only',
              value: AnsweringServiceType.AppointmentDesk,
            },
            { text: 'No Subscription', value: 'ns' },
            { text: 'On', value: 'on' },
          ]
        : [
            { text: 'On', value: 'on' },
            { text: 'N/S', value: 'ns' },
          ],
      filteredValue: subscriptionTagsFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Clear" />
      ),
      render: (subscriptionTag: AnsweringSubscriptionTag) => {
        const { text, color } = getSubscriptionTagTextAndColor(
          subscriptionTag,
          hasValidStripeMlsProductIdForOrg,
        );
        return (
          <StyledSpace>
            <Tag color={color}>{text}</Tag>
          </StyledSpace>
        );
      },

      onCell: addDatacy('brokerages-subscribed'),
      onHeaderCell: addDatacyToHeader('brokerages-subscribed'),
    },
    {
      width: 120,
      sorter: true,
      sortOrder:
        sortDirection && sortField === 'agentCount'
          ? `${sortDirection}end`
          : null,
      showSorterTooltip: true,
      title: '# of Agents',
      key: 'number-of-agents',
      dataIndex: 'agentCount',
      onCell: addDatacy('brokerages-agents'),
      onHeaderCell: addDatacyToHeader('brokerages-agents'),
    },
    {
      width: 150,
      sorter: true,
      sortOrder:
        sortDirection && sortField === 'launchDate'
          ? `${sortDirection}end`
          : null,
      showSorterTooltip: true,
      title: 'Launch Date',
      key: 'launch-date',
      dataIndex: 'launchDate',
      onCell: addDatacy('brokerages-launch-date'),
      onHeaderCell: addDatacyToHeader('brokerages-launch-date'),
      render: (launchDate: Date) => formatDateStr(launchDate),
    },
    {
      width: 100,
      title: '',
      key: 'id',
      dataIndex: 'id',
      align: 'right',
      render: (
        id: string,
        { subscriptionTag }: { subscriptionTag: AnsweringSubscriptionTag },
      ) => (
        <Space>
          <Button
            icon={<Icon type="antdLoginOutlined" />}
            onClick={answeringConfirm(
              { type: 'brokerage', subscriptionTag },
              () => assume(id),
            )}
            data-cy={`brokerage-assume-button-${id}`}
          >
            Assume
          </Button>
        </Space>
      ),
      onCell: addDatacy('brokerages-assume'),
      onHeaderCell: addDatacyToHeader('brokerages-assume'),
    },
  ] as TableProps['columns'])!.filter(Boolean);

  return (
    <SearchTable
      columns={columns}
      dataSource={brokerages}
      onChange={(_pagination, filters, sorter) => {
        setMlsTypesFilter(filters.mlsTypes as string[]);
        setSubscriptionTagsFilter(filters.subscriptionTag as string[]);
        setPlanTypeFilter(filters['plan-type'] as string[]);
        setStatusFilter(filters.status as string[]);
        setSortField((sorter as any).order && (sorter as any).field);
        setSortDirection(
          (sorter as any).order === 'descend'
            ? 'desc'
            : (sorter as any).order === 'ascend'
              ? 'asc'
              : undefined,
        );
      }}
      loading={isLoading}
      dataCy={'brokerages-table'}
      totalItems={total}
    />
  );
};

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSpace = styled(Space)`
  padding: 0 10px;
`;

const StyledTag = styled(Tag)`
  text-transfrom: capitalize;
`;

export default AnsweringBrokerageTable;
